import React, { useEffect, useState } from 'react';

import {
  Grid,
  TextField,
  InputAdornment,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import ClientSideTable, {
  ClientSideTableColumn,
} from '../../../Components/ClientSideTable';
import SearchIcon from '@mui/icons-material/Search';
import { useApiGet } from '../../../utils/httpClientTs';
import { getLCMembers } from '../../../endpoints/chaptersTs';
import DateFilter from '../../../Components/DateFilter';
import dayjs from 'dayjs';

// Define the member data type
interface LCMember {
  id: string;
  first_name: string;
  last_name: string;
  chapter_start_date: string;
  member_start_date: string;
}

// Define the component props
interface LCMembersProps {
  id: string;
  lcAccess: boolean;
  lcAction: boolean;
}

const columns: ClientSideTableColumn<LCMember>[] = [
  {
    id: 'first_name',
    label: 'First Name',
    sortable: true,
  },
  {
    id: 'last_name',
    label: 'Last Name',
    sortable: true,
  },
  {
    id: 'chapter_start_date',
    label: 'Joined Chapter',
    sortable: true,
    render: (value: string) => {
      if (!value) return '';
      // Explicitly parse MM-DD-YYYY format for Safari compatibility
      const parsed = dayjs(value, 'MM-DD-YYYY', true);
      return parsed.isValid() ? parsed.format('MM/DD/YYYY') : 'Invalid Date';
    },
  },
  {
    id: 'member_start_date',
    label: 'Initiation',
    sortable: true,
    render: (value: string) => {
      if (!value) return '';
      // Explicitly parse MM-DD-YYYY format for Safari compatibility
      const parsed = dayjs(value, 'MM-DD-YYYY', true);
      return parsed.isValid() ? parsed.format('MM/DD/YYYY') : 'Invalid Date';
    },
  },
];

const LCMembers: React.FC<LCMembersProps> = ({ id, lcAccess, lcAction }) => {
  const [search, setSearch] = useState('');
  const [data, setData] = useState<LCMember[]>([]);
  const [originalData, setOriginalData] = useState<LCMember[]>([]);
  const [userType, setUserType] = useState('member-active');
  const [dateSince, setDateSince] = useState(dayjs().subtract(10, 'year'));

  const { data: returnData, isFetching } = useApiGet(
    ['lc-members', id, userType, dateSince],
    getLCMembers,
    {
      enabled: true,
      retry: 1,
    },
    { id, userType, dateSince }
  );

  useEffect(() => {
    if (returnData && !isFetching) {
      const membersData = returnData as LCMember[];
      setData(membersData);
      setOriginalData(membersData);
    }
  }, [returnData, isFetching]);

  useEffect(() => {
    // Client side filtering
    const filteredData = originalData?.filter((item) => {
      return (
        item.first_name.toLowerCase().includes(search.toLowerCase()) ||
        item.last_name.toLowerCase().includes(search.toLowerCase())
      );
    });
    setData(filteredData);
  }, [search, originalData]);

  const handleRowClick = (row: LCMember) => {
    window.location.href = `/user/${row.id}`;
  };

  const handleUserTypeChange = (value: string) => {
    setUserType(value);
  };

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  if (!data) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  return (
    <Grid container sx={{ pl: 2, pr: 2 }}>
      <Grid container>
        {lcAction && (
          <Grid item xs={12} sx={{ p: 2 }} textAlign={'right'}>
            <Button
              variant="contained"
              onClick={() => {
                window.location.href = `/user?type=member-all`;
              }}
              color="primary"
              sx={{ fontWeight: 'bold' }}
            >
              Add / Transfer
            </Button>
          </Grid>
        )}
        <Grid item xs={12} sm={6} sx={{ pr: 2, pb: 3 }}>
          <TextField
            label="Search"
            id="search-field"
            size="small"
            value={search}
            fullWidth={true}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={3} sx={{ pr: 2, pb: 3 }}>
          <FormControl fullWidth size={'small'}>
            <InputLabel id="user-type-label">User Type</InputLabel>
            <Select
              labelId="user-type-label"
              id="user-type-select"
              value={userType}
              label="User Type"
              onChange={(event) => handleUserTypeChange(event.target.value)}
            >
              <MenuItem value={'member-all'}>All Members</MenuItem>
              <MenuItem value={'member-active'}>Active Members</MenuItem>
              <MenuItem value={'member-inactive'}>Inactive Members</MenuItem>
              <MenuItem value={'member-deceased'}>Deceased Members</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {['member-all', 'member-inactive', 'member-deceased'].includes(
          userType
        ) && (
          <Grid item xs={12} md={3} sx={{ pr: 2, pb: 3 }}>
            <DateFilter
              label={
                userType === 'member-deceased'
                  ? 'Deceased After'
                  : 'Inactive After'
              }
              filterValue={dateSince}
              updateFilters={setDateSince}
              disabled={false}
            />
          </Grid>
        )}
      </Grid>

      <Grid item xs={12}>
        <ClientSideTable
          data={data}
          columns={columns}
          loading={isFetching}
          onRowClick={handleRowClick}
          emptyMessage="No members found"
          initialRowsPerPage={25}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Grid>
    </Grid>
  );
};

export default LCMembers;
